/**
 * CEO Briefing — One-page macro-analyst aggregating all 14 module analysts
 * into the single screen the CEO / Project Director sees before any meeting.
 *
 *   Header                : Project + status pill + Generate button
 *   Hero one-liner        : the headline sentence
 *   Schedule / Cost pulse : two side-by-side narrative blocks
 *   Top-3 Risks           : ranked, with owner + intervention + by-when
 *   30-Day Priorities     : ordered action list with owners
 *   Module Health Matrix  : 14 modules colour-coded by status
 *
 * Backed by:
 *   POST /api/v1/projects/{pid}/ai-analyst/ceo-briefing?force=true
 *   GET  /api/v1/projects/{pid}/ai-analyst/ceo-briefing/latest
 */
import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useProject } from '@/contexts/ProjectContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Sparkles, Crown, RefreshCw, Loader2, Calendar, DollarSign, AlertOctagon,
  CheckCircle2, Clock, Target, Copy, ChevronRight, TrendingUp, TrendingDown,
  ShieldAlert,
} from 'lucide-react';

const STATUS_META = {
  GREEN:   { bg: 'rgba(16,185,129,0.15)', fg: '#10b981', label: 'GREEN',   ring: 'rgba(16,185,129,0.4)' },
  AMBER:   { bg: 'rgba(245,158,11,0.15)', fg: '#d97706', label: 'AMBER',   ring: 'rgba(245,158,11,0.4)' },
  RED:     { bg: 'rgba(239,68,68,0.15)',  fg: '#dc2626', label: 'RED',     ring: 'rgba(239,68,68,0.4)' },
  UNKNOWN: { bg: 'rgba(113,113,122,0.15)', fg: 'var(--text-muted)', label: 'NO DATA', ring: 'var(--border-color)' },
};
const SEV_COLOR = { CRITICAL: '#dc2626', HIGH: '#ea580c', MEDIUM: '#ca8a04' };

export default function CEOBriefing() {
  const { projectId, projectName, loading: projectLoading } = useProject();
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!projectId) { setBriefing(null); return; }
    setLoading(true);
    api.get(`/v1/projects/${projectId}/ai-analyst/ceo-briefing/latest`)
      .then(r => setBriefing(r.data))
      .catch(e => { if (e?.response?.status !== 404) console.warn(e); setBriefing(null); })
      .finally(() => setLoading(false));
  }, [projectId]);

  const run = async () => {
    if (!projectId) return;
    setRunning(true);
    try {
      // Kick off background job. Backend may return the cached briefing
      // directly (status:'done') or {jobId, status:'pending'} for fresh runs.
      const r = await api.post(`/v1/projects/${projectId}/ai-analyst/ceo-briefing?force=true`,
        {}, { timeout: 30000 });
      if (r.data?.status === 'done' && (r.data?.briefing || r.data?.executive_one_liner)) {
        setBriefing(r.data);
        toast.success('CEO briefing refreshed');
        return;
      }
      const jobId = r.data?.jobId;
      if (!jobId) {
        // Legacy synchronous response — accept as-is.
        setBriefing(r.data);
        toast.success('CEO briefing refreshed');
        return;
      }
      // Poll until done / failed. CEO briefing aggregates 24 analysts so
      // we tolerate a longer ceiling (~5 min) than the single-module panel.
      const POLL_MS = 3000;
      const MAX_TRIES = 100;
      for (let attempt = 0; attempt < MAX_TRIES; attempt++) {
        await new Promise(res => setTimeout(res, POLL_MS));
        try {
          const s = await api.get(`/v1/projects/${projectId}/ai-analyst/ceo-briefing/status/${jobId}`, { timeout: 15000 });
          if (s.data?.status === 'done' && s.data?.briefing) {
            setBriefing(s.data.briefing);
            toast.success('CEO briefing refreshed');
            return;
          }
          if (s.data?.status === 'failed') {
            toast.error(`Briefing failed: ${s.data.error || 'unknown error'}`);
            return;
          }
        } catch (pollErr) {
          console.warn('CEO briefing poll error', pollErr);
        }
      }
      toast.error('Briefing is taking longer than expected — please try again.');
    } catch (e) {
      toast.error(`Briefing failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setRunning(false); }
  };

  const copyJson = () => {
    if (!briefing?.briefing) return;
    navigator.clipboard.writeText(JSON.stringify(briefing.briefing, null, 2));
    toast.success('Copied briefing JSON');
  };

  const b = briefing?.briefing || {};
  const overall = STATUS_META[b.overall_status] || STATUS_META.UNKNOWN;
  const coverage = briefing?.moduleCoverage;

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-4 space-y-4" data-testid="ceo-briefing-page">
        {/* HEADER */}
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-400" />
              <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>CEO Briefing</h1>
              {b.overall_status && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold ml-1"
                      style={{ background: overall.bg, color: overall.fg }}
                      data-testid="ceo-briefing-overall-status">
                  {overall.label}
                </span>
              )}
            </div>
            <p className="text-sm opacity-70" style={{ color: 'var(--text-secondary)' }}>
              60-second executive summary aggregated from module AI analysts · powered by Cognara
            </p>
            {coverage && (
              <p className="text-[10px] opacity-50 mt-1">
                Module coverage: {coverage.withData}/{coverage.total} with data
                {briefing?.generatedAt && ` · generated ${new Date(briefing.generatedAt).toLocaleString()}`}
              </p>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {briefing && (
              <button onClick={copyJson}
                      className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium"
                      style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}
                      data-testid="ceo-briefing-copy">
                <Copy className="w-3 h-3" /> Copy JSON
              </button>
            )}
            <button onClick={run} disabled={running || !projectId}
                    className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold"
                    style={{ background: '#06b6d4', color: '#000', opacity: (running || !projectId) ? 0.6 : 1 }}
                    data-testid="ceo-briefing-run">
              {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              {briefing ? 'Refresh briefing' : 'Generate briefing'}
            </button>
          </div>
        </div>

        {!projectId && !projectLoading && (
          <EmptyState title="Pick a project first"
            sub="The CEO briefing is project-scoped. Open a project from the Portfolio dashboard, then return here." />
        )}

        {projectId && loading && (
          <div className="py-10 text-center opacity-60" data-testid="ceo-briefing-loading">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            <div className="text-xs mt-2">Loading cached briefing…</div>
          </div>
        )}

        {projectId && !loading && !briefing && (
          <EmptyState title="No briefing yet"
            sub="Click ‘Generate briefing’ to run the macro-analyst. It will aggregate all 14 module reports into a single executive summary." />
        )}

        {briefing && (
          <>
            {/* HERO ONE-LINER */}
            <div className="rounded-xl p-5"
                 style={{ background: `linear-gradient(135deg, ${overall.fg}11, transparent)`,
                          border: `1px solid ${overall.ring}` }}
                 data-testid="ceo-briefing-one-liner">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: overall.fg }} />
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1">Executive headline</div>
                  <div className="text-lg font-semibold leading-snug" style={{ color: 'var(--text-primary)' }}>
                    {b.executive_one_liner || '—'}
                  </div>
                  {b.overall_narrative && (
                    <div className="text-sm mt-2 opacity-90" style={{ color: 'var(--text-secondary)' }}>
                      {b.overall_narrative}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* SCHEDULE + COST PULSE */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <PulseCard label="Schedule pulse" icon={Calendar} color="#3b82f6"
                         content={b.schedule_pulse} testid="ceo-briefing-schedule-pulse" />
              <PulseCard label="Cost pulse" icon={DollarSign} color="#10b981"
                         content={b.cost_pulse} testid="ceo-briefing-cost-pulse" />
            </div>

            {/* TOP-3 RISKS */}
            {Array.isArray(b.top_risks) && b.top_risks.length > 0 && (
              <div className="rounded-xl p-4"
                   style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}
                   data-testid="ceo-briefing-top-risks">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldAlert className="w-4 h-4 text-red-400" />
                  <h2 className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Top 3 Risks · Ranked</h2>
                  <span className="text-[10px] opacity-60">CEO must authorise interventions today</span>
                </div>
                <div className="space-y-3">
                  {b.top_risks.map((r, idx) => {
                    const sev = SEV_COLOR[r.severity] || '#f97316';
                    return (
                      <div key={idx} className="rounded-lg p-3"
                           style={{ background: 'var(--surface-bg)', border: `1px solid ${sev}55` }}
                           data-testid={`ceo-briefing-risk-${idx}`}>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-bold text-base"
                               style={{ background: sev + '22', color: sev, border: `1px solid ${sev}77` }}>
                            #{r.rank || idx + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold"
                                    style={{ background: sev + '33', color: sev }}>
                                {r.severity || 'HIGH'}
                              </span>
                              <span className="text-[10px] opacity-60 font-mono">{r.module}</span>
                            </div>
                            <div className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{r.risk}</div>
                            {r.evidence && (
                              <div className="text-[11px] mt-1 font-mono" style={{ color: 'var(--text-muted)' }}>→ {r.evidence}</div>
                            )}
                            {r.impact && (
                              <div className="text-[11px] mt-1.5" style={{ color: 'var(--text-secondary)' }}><b style={{ color: 'var(--text-muted)' }}>Impact:</b> <span>{r.impact}</span></div>
                            )}
                            <div className="rounded p-2 mt-2 flex items-start gap-2"
                                 style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)' }}>
                              <Target className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: '#10b981' }} />
                              <div className="flex-1">
                                <div className="text-[11px] font-semibold" style={{ color: '#10b981' }}>{r.intervention}</div>
                                <div className="text-[10px] mt-0.5 flex flex-wrap gap-2" style={{ color: 'var(--text-muted)' }}>
                                  <span><b>Owner:</b> {r.owner || '—'}</span>
                                  <span><b>By:</b> {r.by_when || '—'}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 30-DAY PRIORITIES */}
            {Array.isArray(b.next_30_day_priorities) && b.next_30_day_priorities.length > 0 && (
              <div className="rounded-xl p-4"
                   style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}
                   data-testid="ceo-briefing-30day">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-cyan-400" />
                  <h2 className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Next 30-Day Priorities</h2>
                </div>
                <div className="space-y-2">
                  {b.next_30_day_priorities.map((p, idx) => (
                    <div key={idx} className="rounded p-2 flex items-start gap-3"
                         style={{ background: 'var(--surface-bg)', border: '1px solid var(--border-subtle)' }}
                         data-testid={`ceo-briefing-priority-${idx}`}>
                      <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold"
                           style={{ background: 'rgba(6,182,212,0.15)', color: '#06b6d4' }}>
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-semibold" style={{ color: 'var(--text-primary)' }}>{p.priority}</div>
                        <div className="text-[10px] mt-0.5 flex flex-wrap gap-2" style={{ color: 'var(--text-muted)' }}>
                          <span><b>Owner:</b> {p.owner || '—'}</span>
                          {p.expected_outcome && <span style={{ color: '#10b981' }}>→ {p.expected_outcome}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* MODULE HEALTH MATRIX */}
            {Array.isArray(b.module_health_summary) && b.module_health_summary.length > 0 && (
              <div className="rounded-xl p-4"
                   style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}
                   data-testid="ceo-briefing-module-health">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-cyan-400" />
                  <h2 className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Module Health Matrix · 14 analysts</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {b.module_health_summary.map((m, idx) => {
                    const meta = STATUS_META[m.status] || STATUS_META.UNKNOWN;
                    return (
                      <div key={idx} className="rounded p-2"
                           style={{ background: 'var(--surface-bg)', border: `1px solid ${meta.ring}` }}
                           data-testid={`ceo-briefing-module-${m.module}`}>
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.fg }} />
                          <span className="font-semibold text-[11px]" style={{ color: 'var(--text-primary)' }}>{m.label || m.module}</span>
                          <span className="ml-auto px-1.5 py-0.5 rounded text-[9px] font-bold"
                                style={{ background: meta.bg, color: meta.fg }}>{meta.label}</span>
                        </div>
                        <div className="text-[10px] leading-snug" style={{ color: 'var(--text-muted)' }}>{m.headline || '—'}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="text-[10px] opacity-50 text-right pt-2">
              Cached 1h · Powered by Cognara · Aggregates all module AI analysts
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function PulseCard({ label, icon: Icon, color, content, testid }) {
  return (
    <div className="rounded-xl p-3"
         style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}
         data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-60 mb-1">
        <Icon className="w-3 h-3" style={{ color }} />
        {label}
      </div>
      <div className="text-[12px] leading-relaxed" style={{ color: 'var(--text-primary)' }}>
        {content || '—'}
      </div>
    </div>
  );
}

function EmptyState({ title, sub }) {
  return (
    <div className="rounded-xl p-8 text-center"
         style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}
         data-testid="ceo-briefing-empty">
      <Crown className="w-8 h-8 mx-auto opacity-50 text-amber-400" />
      <div className="mt-3 font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</div>
      <div className="text-xs opacity-70 mt-1" style={{ color: 'var(--text-secondary)' }}>{sub}</div>
    </div>
  );
}
